/**
 * Turning a photograph into what gets stored, entirely in the browser.
 *
 * This is the part that makes end-to-end encryption affordable. The server used
 * to decode each upload and produce the thumbnail and preview; it cannot any
 * more, having no key. If nobody replaced that work the gallery would have to
 * serve full originals — a hundred twelve-megabyte files to draw one screen of
 * tiles — so the browser does it here, before anything is sealed.
 *
 * The order matters and is not interchangeable: **resize first, encrypt after**.
 * Ciphertext cannot be resized, so a file sealed before its derivatives exist
 * has none, and nothing downstream can make them.
 *
 * ── What this costs, and what it buys ──
 *
 * A phone now spends a second or two per photograph decoding and re-encoding
 * before the upload starts, where it used to spend none. In exchange the bytes
 * that leave the device are already unreadable, and stay unreadable in storage,
 * in the database, and in any backup either of them appears in.
 *
 * ── Why WebP and not AVIF ──
 *
 * Browsers decode AVIF and none of them encode it. The server did, which is how
 * the eager ladder produced AVIF at all. Moving the work here means WebP for
 * everything — slightly larger files, no visible difference at these sizes.
 */

import {
  IMAGE_VARIANT_LADDER,
  bytesToHex,
  encrypt,
  encryptedLength,
  generateSymmetricKey,
} from "@fc/core";

export interface PreparedVariant {
  variant: "thumb" | "preview";
  format: "webp";
  width: number;
  height: number;
  /** Already sealed. Its plaintext never leaves this module. */
  ciphertext: Uint8Array;
}

export interface PreparedUpload {
  /** The original file, sealed. */
  original: Uint8Array;
  /**
   * SHA-256 of the sealed bytes, as hex.
   *
   * What the server records and what the object key is derived from. The hash
   * of the *plaintext* is never sent: it would let anyone holding the database
   * test a photograph they already have against every file in here.
   */
  sealedSha256: string;
  /** Plaintext byte length, for the quota reservation and for display. */
  originalPlaintextBytes: number;
  variants: PreparedVariant[];
  /** The file key, sealed with the group key. The only form the server sees. */
  wrappedFileKey: string;
  width: number | null;
  height: number | null;
}

/**
 * How a file key is sealed for the group it belongs to.
 *
 * Passed in rather than taking the group key directly, so this module never
 * holds a key that opens more than the one file it is working on. What it is
 * given can seal; it cannot open anything.
 */
export type SealFileKey = (fileKey: Uint8Array) => Promise<string>;

/**
 * Raised when this browser cannot open the file, so the caller can choose what
 * to do about it rather than treating it as a failed upload.
 */
export class UndecodableError extends Error {
  constructor(mime: string) {
    super(`This browser cannot open ${mime || "that file"}.`);
    this.name = "UndecodableError";
  }
}

/**
 * Image formats worth *attempting* to take end to end.
 *
 * HEIC is here and it is the interesting case. iPhones shoot it by default, and
 * whether a browser can decode it is not a fact about the format — Safari can,
 * on exactly the devices that produce it, and Chrome cannot. Leaving it out
 * meant every iPhone photograph took the unencrypted path, on the platform that
 * generates most of them: a hole in the main promise, on the majority of the
 * cameras.
 *
 * So it is attempted, and `prepareUpload` reports honestly when the decode
 * fails. Guessing from the user agent would be a worse version of the same
 * question, asked without looking.
 */
const DECODABLE = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

/**
 * Whether this file can be handled without the server ever seeing it.
 *
 * Anything else — video, HEIC outside Safari, formats nobody agrees on — has to
 * take the weaker path where a worker holds a key, and the interface says so
 * rather than implying both are the same.
 */
export function canEncryptEndToEnd(mime: string): boolean {
  /*
   * The resolved mime, not `file.type`.
   *
   * A `File` from the iOS photo library can arrive with an empty `type`, and
   * reading the answer off it meant a HEIC that Safari could perfectly well
   * decode was treated as unsealable — taking the clear path on the platform
   * that shoots most of the photographs. The caller has already resolved the
   * type from the filename where the browser did not supply one.
   */
  return DECODABLE.has(mime);
}

/**
 * Decode once and reuse.
 *
 * `createImageBitmap` hands back a decoded frame that can be drawn repeatedly,
 * so two derivatives cost one decode rather than two. On a large photograph the
 * decode is most of the work.
 */
async function decode(bytes: Uint8Array, mime: string): Promise<ImageBitmap> {
  try {
    /*
     * Decoded from bytes already in memory rather than from the `File`.
     *
     * A `File` picked from the iOS photo library is a handle to something the
     * system may move, re-encode or reclaim underneath us. Reading it once and
     * working from the result removes two of the three reads this upload used
     * to perform — hash, decode, and read-for-sealing were each a separate trip
     * to a handle that had to still be valid — and with them the window in
     * which a long batch loses a file it had already accepted.
     */
    return await createImageBitmap(
      new Blob([bytes as unknown as BlobPart], { type: mime }),
    );
  } catch {
    // Distinguished from an ordinary failure: the caller has somewhere else to
    // go, and reporting "upload failed" for a photograph that is perfectly
    // fine would be both wrong and unactionable.
    throw new UndecodableError(mime);
  }
}

function scaleToFit(
  bitmap: ImageBitmap,
  maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(bitmap.width, bitmap.height);
  // Never upscale: a 400px photograph must not become a 2048px one that is
  // larger than its own original and no sharper.
  const ratio = longest <= maxEdge ? 1 : maxEdge / longest;
  return {
    width: Math.max(1, Math.round(bitmap.width * ratio)),
    height: Math.max(1, Math.round(bitmap.height * ratio)),
  };
}

async function resize(
  bitmap: ImageBitmap,
  maxEdge: number,
  quality: number,
): Promise<{ bytes: Uint8Array; width: number; height: number }> {
  const { width, height } = scaleToFit(bitmap, maxEdge);

  const canvas = new OffscreenCanvas(width, height);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser cannot resize images.");

  /*
   * Quality hints, not guarantees. Downscaling by a large factor in one step
   * aliases badly on some engines; these ask for the better path where it
   * exists and are ignored where it does not.
   */
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(bitmap, 0, 0, width, height);

  const blob = await canvas.convertToBlob({ type: "image/webp", quality });
  return { bytes: new Uint8Array(await blob.arrayBuffer()), width, height };
}

/**
 * Produce everything the server will hold for one photograph, already sealed.
 *
 * The file key is generated here, used once, and sealed before it is returned
 * — so the caller never holds it in a form it could accidentally send.
 */
/**
 * Make the derivatives for a photograph whose original is already stored.
 *
 * The repair path. When a file's thumbnails are lost, the server cannot make
 * new ones — it has no key, and a worker handed ciphertext produces a failed
 * job rather than a picture. Only a browser holding the group key can, so this
 * is the same resizing the upload does, against bytes that came back *out* of
 * storage instead of off a disk.
 *
 * The existing file key is passed in rather than generated: the original is not
 * being re-sealed, only the small copies beside it, and they have to open with
 * the key the file already has.
 */
export async function prepareVariants(
  originalBytes: Uint8Array,
  mime: string,
  fileKey: Uint8Array,
): Promise<PreparedVariant[]> {
  const bitmap = await decode(originalBytes, mime);
  try {
    return await renderVariants(bitmap, fileKey);
  } finally {
    bitmap.close();
  }
}

/** The eager rungs of the ladder, resized and sealed. Shared by both paths. */
async function renderVariants(
  bitmap: ImageBitmap,
  fileKey: Uint8Array,
): Promise<PreparedVariant[]> {
  const variants: PreparedVariant[] = [];

  // Only the rungs the ladder marks eager. `preview_hq` is generated on
  // demand and has no equivalent here yet.
  for (const rung of IMAGE_VARIANT_LADDER.filter((entry) => entry.eager)) {
    const webp = rung.formats.find((format) => format.format === "webp");
    if (!webp) continue;

    const resized = await resize(bitmap, rung.maxEdge, webp.quality / 100);
    variants.push({
      variant: rung.variant as "thumb" | "preview",
      format: "webp",
      width: resized.width,
      height: resized.height,
      ciphertext: await encrypt(resized.bytes, fileKey),
    });
  }

  return variants;
}

export async function prepareUpload(
  /** The file's bytes, read once by the caller. See `decode`. */
  originalBytes: Uint8Array,
  mime: string,
  seal: SealFileKey,
): Promise<PreparedUpload> {
  const bitmap = await decode(originalBytes, mime);
  const fileKey = generateSymmetricKey();

  /*
   * The frame's dimensions, taken before it is released. Reading them off the
   * bitmap further down would mean holding the bitmap further down, and holding
   * it is the expensive part.
   */
  const width = bitmap.width;
  const height = bitmap.height;

  let released = false;
  const releaseFrame = () => {
    if (released) return;
    released = true;
    // Decoded frames live outside the JavaScript heap and are not collected by
    // dropping the reference.
    bitmap.close();
  };

  try {
    const variants = await renderVariants(bitmap, fileKey);

    /*
     * The frame goes before the original is read.
     *
     * A twelve-megapixel photograph decodes to roughly 48 MB of raw pixels, and
     * nothing below this line looks at them — the derivatives are already made.
     * Holding it through reading the file and sealing it meant carrying 48 MB
     * of frame plus the file plus its ciphertext at once, three uploads at a
     * time. That is where a batch import on a phone gets killed.
     */
    releaseFrame();

    const sealedOriginal = await encrypt(originalBytes, fileKey);
    const sealedSha256 = bytesToHex(
      new Uint8Array(
        await crypto.subtle.digest("SHA-256", sealedOriginal as BufferSource),
      ),
    );

    return {
      original: sealedOriginal,
      sealedSha256,
      originalPlaintextBytes: originalBytes.length,
      variants,
      wrappedFileKey: await seal(fileKey),
      width,
      height,
    };
  } finally {
    // Already released on the happy path; this is for the failures.
    releaseFrame();
  }
}

/** Sealed size for a plaintext length, so a reservation can be taken up front. */
export function sealedSize(plaintextBytes: number): number {
  return encryptedLength(plaintextBytes);
}
